import {useEffect, useRef, useState} from "react";
import throttle from "lodash/throttle";

/**
 * Returns a throttled version of the given value.
 * Fires immediately on the first change (leading) and once more
 * at the end of the throttle window if changes came in (trailing).
 *
 * Useful for collapsing bursts of form state changes (e.g. datagrid edits)
 * into a single recalculation while keeping the UI feeling responsive.
 */
export function useThrottled<T>(value: T, delayMs: number): T {
    const [throttled, setThrottled] = useState(value);

    const throttledSet = useRef(
        throttle((v: T) => setThrottled(v), delayMs, {leading: true, trailing: true})
    ).current;

    useEffect(() => {
        throttledSet(value);
        return () => throttledSet.cancel();
    }, [value]);

    return throttled;
}
